// Quotation totals — same rules as the server (routes/quotationRoutes.js totalsOf):
//   item amount = rate × qty ; subtotal − discount = taxable value
//   Calculate GST on : item GST = taxable × quotation GST % ; each additional charge marked
//                      "With GST" adds amount × its own GST % ; "Without GST" adds no GST
//   Calculate GST off: no GST anywhere (items or charges)
//   grand total = taxable + item GST + charges + GST on charges
export const GST_RATES = [0, 5, 12, 18, 28];
export const CHARGE_SUGGESTIONS = ['Transportation', 'Loading', 'Unloading', 'Packaging', 'Installation', 'Other charges'];

export const r2 = n => Math.round((Number(n) || 0) * 100) / 100;
const pos = v => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0; };
export const isOn = v => !(v === false || v === 0 || v === '0' || v === 'false');
export const withGstOf = c => !(c?.with_gst === false || c?.with_gst === 0 || c?.with_gst === '0' || c?.with_gst === 'false' || c?.with_gst === 'without');

export function quoteTotals({ items = [], charges = [], calc_gst = true, discount_amount = 0, gst_percent = 0 }) {
  const calc = isOn(calc_gst);
  const subtotal = r2(items.reduce((a, it) => a + r2(pos(it.qty) * pos(it.rate)), 0));
  const discount = Math.min(subtotal, r2(pos(discount_amount)));
  const taxable = r2(subtotal - discount);
  const rate = Math.min(100, pos(gst_percent));
  const itemGst = calc ? r2(taxable * rate / 100) : 0;
  const chargeRows = charges
    .filter(c => String(c.description || '').trim() || pos(c.amount) > 0)
    .map(c => {
      const amount = r2(pos(c.amount));
      const withGst = withGstOf(c);
      const gstPercent = Math.min(100, pos(c.gst_percent));
      const gst = calc && withGst ? r2(amount * gstPercent / 100) : 0;
      return { ...c, amount, with_gst: withGst, gst_percent: gstPercent, gst_amount: gst, line_total: r2(amount + gst) };
    });
  const chargesAmount = r2(chargeRows.reduce((a, c) => a + c.amount, 0));
  const chargesGst = r2(chargeRows.reduce((a, c) => a + c.gst_amount, 0));
  // GST grouped by rate (for the CGST / SGST or IGST lines)
  const byRate = new Map();
  const addTax = (r, base, gst, src) => {
    if (!(gst > 0)) return;
    const k = `${src}|${r}`;
    if (!byRate.has(k)) byRate.set(k, { src, rate: r, base: 0, gst: 0 });
    const x = byRate.get(k); x.base = r2(x.base + base); x.gst = r2(x.gst + gst);
  };
  addTax(rate, taxable, itemGst, 'items');
  chargeRows.forEach(c => addTax(c.gst_percent, c.amount, c.gst_amount, 'charges'));
  const gst = r2(itemGst + chargesGst);
  return {
    calc, subtotal, discount, taxable, rate, itemGst, chargeRows, chargesAmount, chargesGst, gst,
    taxLines: [...byRate.values()], total: r2(taxable + itemGst + chargesAmount + chargesGst),
  };
}

// CGST + SGST halves (intra-state) or one IGST line (inter-state)
export const splitGst = (gst, type) => {
  if (type === 'inter') return [{ label: 'IGST', amount: r2(gst) }];
  const c = r2(gst / 2);
  return [{ label: 'CGST', amount: c }, { label: 'SGST', amount: r2(gst - c) }];
};
